import { Component, Input, OnChanges, OnDestroy, OnInit, inject, input } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

import { CurrencyPipe, CurrencySymbolPipe } from '@frontend/vanilla/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AppConstants } from '../../../config/appConstants';
import { DepositInfo } from '../../../model/depositInfo.model';
import { QDInstrument, TxnLimitInfo } from '../../../model/qdInstrument.model';
import { BehaviorDataService } from '../../../service/behaviorData.service';
import { DepositService } from '../../../service/deposit.service';
import { DepositRedirectService } from '../../../service/depositRedirect.service';
import { GtmService } from '../../../service/gtm.service';
import { ResizeService } from '../../../service/resize.service';
import { UserDataService } from '../../../service/userDataService';
import {
    ExpiredKycLimitValidator,
    amlThresholdExcValidator,
    amlThresholdToBeExcValidator,
    annRekycLimitExcValidator,
    spainODValidator,
    spainRekycValidator,
} from '../../../shared/validators/custom.validator';
import { prepaidLimit } from '../../../shared/validators/prepaidLimit.validator';
import { providerBalanceValidator } from '../../../shared/validators/providerbalance.validator';
import { selfSetLimit } from '../../../shared/validators/selfsetlimit.validator';

@Component({
    selector: 'app-amount',
    templateUrl: './amount.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatHint,
        MatError,
        TranslateModule,
        CurrencyPipe,
        CurrencySymbolPipe,
    ],
})
export class AmountComponent implements OnInit, OnChanges, OnDestroy {
    private userDataService = inject(UserDataService);
    private depositService = inject(DepositService);
    depositRedirectService = inject(DepositRedirectService);
    private behaviorData = inject(BehaviorDataService);
    private gtmService = inject(GtmService);
    private resizeService = inject(ResizeService);

    @Input() amountFormGroup: FormGroup;
    selectedOption = input.required<QDInstrument>();

    depositInfo: DepositInfo;
    minLimit: number;
    maxLimit: number;
    showMaxLimit = false;
    lastUsedAmount: number;

    showAmountError = false;
    selfLimits: TxnLimitInfo;
    maxDepositLimit: string;
    minDepositLimit: string;
    depositResponseAmountPrefilled = false;
    rgLimitData: any;
    exchangeRate: number;
    ccDepositLimitCheckEnabled: boolean;
    limitPeriod: string;
    ccCurrentDepositLimit: string;
    subscriptions$: Subscription[] = [];
    enablePrepaidcardRestrictions: boolean;
    isPrepaidLimitExceeded: boolean = false;
    rollingWeekRemainingAmount: any;

    constructor() {
        this.depositInfo = this.depositService.depositInfo;
    }

    ngOnInit() {
        if (this.depositInfo?.amountInAccountCurrency === '') {
            this.depositService.depositInfo.amountInAccountCurrency = '';
        }
        this.enablePrepaidcardRestrictions = this.userDataService?.userData?.configParams?.enable_prepaidcard_restrictions == 'Y' ? true : false;
        this.rollingWeekRemainingAmount = Number(this.userDataService?.userData?.configParams?.rollingweek_remaining_balance);
        this.isPrepaidLimitExceeded = Number(this.userDataService?.userData?.configParams?.rollingweek_remaining_balance) == 0;
        this.initialize();
        this.getBehaviorData();
    }

    ngOnChanges() {
        this.initialize();
    }

    initialize() {
        this.maxDepositLimit = '0';
        this.amountFormGroup.addControl('userAmount', new FormControl(''));
        this.showAmountError = true;
        if (this.selectedOption()?.paymentMethodInfo?.txnLimitInfo) {
            this.selfLimits = this.selectedOption()?.paymentMethodInfo?.txnLimitInfo;
            let minLimit = this.selectedOption()?.paymentMethodInfo?.txnLimitInfo?.minLimit?.value;
            let maxLimit = this.selectedOption()?.paymentMethodInfo?.txnLimitInfo?.maxLimit?.value;
            let lastUsedAmount = this.selectedOption()?.paymentMethodInfo?.lastUsedAmount;
            const txnCurrency = this.depositInfo?.amountInfo?.nativeCurrency;

            if (maxLimit) {
                this.maxDepositLimit = parseFloat(maxLimit).toFixed(2);
            }

            if (minLimit) {
                this.minDepositLimit = parseFloat(minLimit).toFixed(2);
            }

            const exponentZeroCurrencies = this.selectedOption()?.paymentAccountInfo?.exponentZeroCurrencies;
            if (exponentZeroCurrencies) {
                const exponentZeroCurrenciesList = exponentZeroCurrencies.split(',');
                if (exponentZeroCurrenciesList.indexOf(txnCurrency) > -1) {
                    lastUsedAmount = parseFloat(lastUsedAmount).toFixed(0);
                    minLimit = parseFloat(minLimit).toFixed(0);
                    maxLimit = parseFloat(maxLimit).toFixed(0);
                } else {
                    lastUsedAmount = parseFloat(lastUsedAmount).toFixed(2);
                    minLimit = parseFloat(minLimit).toFixed(2);
                    maxLimit = parseFloat(maxLimit).toFixed(2);
                }
            }

            if (!this.depositResponseAmountPrefilled) {
                if (parseFloat(lastUsedAmount) < parseFloat(maxLimit)) {
                    if (parseFloat(lastUsedAmount) < parseFloat(minLimit)) {
                        this.depositService.depositInfo.amountInAccountCurrency = minLimit;
                    } else {
                        this.depositService.depositInfo.amountInAccountCurrency = lastUsedAmount;
                    }
                } else {
                    this.depositService.depositInfo.amountInAccountCurrency = '';
                }
                this.depositInfo.amountInAccountCurrency = this.depositService?.depositInfo?.amountInAccountCurrency;
                this.behaviorData.changeEnteredAmount(this.depositInfo?.amountInAccountCurrency);
            } else {
                this.validateAmount();
            }
        } else {
            this.validateAmount();
        }

        this.showMaxLimit = parseFloat(this.maxDepositLimit) != 0 && parseFloat(this.maxDepositLimit) >= parseFloat(this.minDepositLimit);
        this.ccDepositLimitCheckEnabled = this.userDataService?.userData?.configParams?.isCreditCardDepositLimitCheckEnabled === 'Y';
        this.updateNativeCurrency();
        if (this.userDataService?.userData?.configParams?.colombia_deposit_tax_enabled == 'Y') {
            this.subscriptions$.push(
                this.amountFormGroup?.controls?.userAmount?.valueChanges?.subscribe(() => {
                    this.depositService.calculateVATPercentage(this.amountFormGroup?.controls?.userAmount?.value);
                }) as Subscription,
            );
        }
    }

    validateAmount() {
        this.minLimit = this.selectedOption()?.paymentMethodInfo?.minDepositLimit?.value;
        this.maxLimit = this.selectedOption()?.paymentMethodInfo?.maxDepositLimit?.value;

        this.amountFormGroup?.controls?.userAmount?.setValidators(
            Validators.compose([
                Validators.required,
                Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
                Validators.min(this.minLimit),
                Validators.max(parseFloat(this.maxDepositLimit)),
            ]),
        );

        this.depositService.setAmountCharged();
        this.depositService.getAmountInTransactionCurrency();

        this.showAmountError = true;
        if (parseFloat(this.depositInfo?.amountInAccountCurrency) < parseFloat(this.minDepositLimit)) {
            this.amountFormGroup?.controls?.userAmount?.removeValidators([Validators.min(this.minLimit)]);
            this.amountFormGroup?.controls?.userAmount?.updateValueAndValidity();
        }

        if (this.selfLimits?.type === 'txn_limit') {
            if (parseFloat(this.depositInfo?.amountInAccountCurrency) > parseFloat(this.maxDepositLimit)) {
                this.amountFormGroup?.controls?.userAmount?.removeValidators([Validators.max(this.maxLimit)]);
                this.amountFormGroup?.controls?.userAmount?.updateValueAndValidity();
            }
        }

        if (this.selectedOption().accountBalance) {
            if (parseFloat(this.depositInfo?.amountInAccountCurrency) > this.selectedOption()?.accountBalance) {
                this.amountFormGroup?.controls?.userAmount?.setValidators(
                    Validators.compose([
                        Validators.required,
                        Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
                        Validators.min(this.minLimit),
                        Validators.max(parseFloat(this.maxDepositLimit)),
                        providerBalanceValidator(true),
                    ]),
                );
            }
        }

        if (this.depositInfo?.isBelgiumRgDecreeEnabled) {
            this.minLimit = parseFloat(this.selectedOption()?.paymentMethodInfo?.txnLimitInfo?.minLimit?.value);
            this.maxLimit = parseFloat(this.selectedOption()?.paymentMethodInfo?.txnLimitInfo?.maxLimit?.value);
        }
        this.validateSelfSetLimits();
        this.validatePrepaidLimit();
        this.validateOccuDetails();
        this.combinedValidator();
    }

    validateSelfSetLimits() {
        if (
            this.selfLimits &&
            (this.selfLimits?.type.includes('purchase_limit_') ||
                this.selfLimits?.type.includes('rg_limit_') ||
                this.selfLimits?.type.includes('svoc_limit_') ||
                this.selfLimits?.type.includes('overall_deposit_limit_'))
        ) {
            if (
                +this.depositInfo?.amountInAccountCurrency >= this.selectedOption()?.paymentMethodInfo?.minDepositLimit?.value &&
                +this.depositInfo?.amountInAccountCurrency > +this.maxDepositLimit
            ) {
                this.selfLimitValidation();
                this.showAmountError = false;
            } else {
                this.showAmountError = true;
            }
            this.limitPeriod = this.selfLimits?.type.substring(this.selfLimits?.type.lastIndexOf('_') + 1);
        }
        if (this.ccDepositLimitCheckEnabled && this.selectedOption()?.creditCard) {
            if (
                this.depositInfo?.amountInAccountCurrency < this.selectedOption()?.paymentMethodInfo?.txnLimitInfo?.minLimit?.value &&
                this.ccCurrentDepositLimit < this.selectedOption()?.paymentMethodInfo?.txnLimitInfo?.minLimit?.value
            ) {
                this.showAmountError = false;
                this.selfLimitValidation();
                this.behaviorData.changeshowCCDepositLimitCheckMsg(true);
            } else if (
                parseFloat(this.depositInfo?.amountInAccountCurrency) >
                parseFloat(this.selectedOption()?.paymentMethodInfo?.txnLimitInfo?.maxLimit?.value)
            ) {
                this.behaviorData.changeshowCCDepositLimitCheckMsg(false);
                this.selfLimitValidation();
                this.showAmountError = false;
            } else if (parseFloat(this.depositInfo?.amountInAccountCurrency) > parseFloat(this.ccCurrentDepositLimit)) {
                this.behaviorData.changeshowCCDepositLimitCheckMsg(true);
                this.selfLimitValidation();
                this.showAmountError = false;
            } else {
                this.showAmountError = true;
            }
        }
        this.resizeService.resizeCashier();
    }
    validatePrepaidLimit() {
        if (
            this.enablePrepaidcardRestrictions &&
            this.selectedOption()?.fundingSource?.toLowerCase() == AppConstants.PREPAID &&
            !this.isPrepaidLimitExceeded
        ) {
            if (Number(this.maxDepositLimit) > this.rollingWeekRemainingAmount) {
                this.maxDepositLimit = this.rollingWeekRemainingAmount;
                this.amountFormGroup?.controls?.userAmount?.setValidators(
                    Validators.compose([
                        Validators.required,
                        Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
                        Validators.min(this.minLimit),
                        Validators.max(parseFloat(this.maxDepositLimit)),
                        prepaidLimit(true),
                    ]),
                );
                this.amountFormGroup.controls?.userAmount?.updateValueAndValidity();
            }
        }
    }

    onChangeAmt() {
        this.behaviorData.changeEnteredAmount(this.depositInfo?.amountInAccountCurrency);
        this.validateSelfSetLimits();
        this.amountFormGroup?.controls?.userAmount?.markAsTouched();
        this.amountFormGroup.controls?.userAmount?.updateValueAndValidity();
    }
    onAmountBlur() {
        if (this.amountFormGroup?.controls?.userAmount?.errors) {
            // GTM Events:  Scenario: Users clicks has an enter amount error
            this.gtmService.paymentErrorEvent('Please enter Amount');
        }
        if (
            this.amountFormGroup?.controls?.userAmount?.hasError('max') &&
            Number(this.depositInfo?.amountInAccountCurrency) > this.maxLimit &&
            this.maxLimit > 0 &&
            !this.selfLimits?.type.includes('svoc_limit_')
        ) {
            // GTM Events:  limits exceeded
            this.gtmService.paymentErrorEvent('limits exceeded');
        }
    }

    updateNativeCurrency() {
        if (this.depositInfo?.amountInAccountCurrency) {
            if (this.depositInfo?.amountInfo?.nativeCurrency !== this.selectedOption()?.paymentMethodInfo?.minDepositLimit?.currencyCode) {
                this.depositService.depositInfo.amountInfo.nativeAmountString = this.depositService.getAmountInTransactionCurrency();
            } else {
                this.depositService.depositInfo.amountInfo.nativeAmountString = parseFloat(this.depositInfo?.amountInAccountCurrency).toFixed(2);
            }
            if (this.selectedOption()?.paymentMethodInfo?.formattedFee) {
                if (this.selectedOption()?.paymentMethodInfo?.feeType === 'abs') {
                    this.depositService.depositInfo.amountCharged = (
                        parseFloat(this.depositInfo?.amountInAccountCurrency) + parseFloat(this.selectedOption().paymentMethodInfo.formattedFee)
                    ).toFixed(2);
                } else if (parseFloat(this.selectedOption()?.paymentMethodInfo?.formattedFee) > 0) {
                    const feeAmount =
                        (parseFloat(this.depositInfo?.amountInAccountCurrency) * parseFloat(this.selectedOption()?.paymentMethodInfo?.formattedFee)) /
                        100;
                    this.depositService.depositInfo.amountCharged = (parseFloat(this.depositInfo?.amountCharged) + feeAmount).toFixed(2);
                }
                this.resizeService.resizeCashier();
            }
        } else {
            this.depositService.depositInfo.amountInfo.nativeAmountString = '';
            this.depositService.depositInfo.amountCharged = '';
        }
    }

    keyPressNumbersDecimal(event: any) {
        const charCode = event.which ? event.which : event.keyCode;
        if (charCode !== 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
            event.preventDefault();
            return false;
        }
        return true;
    }

    selfLimitValidation() {
        if (!this.amountFormGroup?.controls?.userAmount?.hasError('expiredKycLimitValidation')) {
            this.amountFormGroup?.controls?.userAmount?.setValidators(
                Validators.compose([
                    Validators.required,
                    Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
                    Validators.min(this.minLimit),
                    Validators.max(parseFloat(this.maxDepositLimit)),
                    selfSetLimit(true),
                ]),
            );
            this.amountFormGroup.controls?.userAmount?.updateValueAndValidity();
        }
    }

    getBehaviorData() {
        this.subscriptions$.push(
            this.behaviorData.getEnteredAmount.subscribe((data) => {
                this.depositInfo.amountInAccountCurrency = data;
                this.depositService.setAmountCharged();
                this.depositService.getAmountInTransactionCurrency();
                this.amountFormGroup?.controls?.userAmount?.updateValueAndValidity();
                this.validateAmount();
            }),
        );
    }
    validateRekycLimit() {
        if (
            this.userDataService?.userData?.configParams?.spainRekycEnabled == 'Y' &&
            this.userDataService?.userData?.configParams?.rekycSubErrorCode == '502' &&
            this.userDataService?.userData?.configParams?.spain_rekycFailed == 'false' &&
            (Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit) <
                Number(this.selectedOption()?.paymentMethodInfo?.minDepositLimit?.value) ||
                Number(this.depositInfo?.amountInAccountCurrency) > Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit))
        ) {
            return { spainRekycLimit: true };
        }
        return null;
    }

    validateOccuDetails() {
        if (
            this.userDataService?.userData?.configParams?.isPlayerJobDetailsUpdated == 'N' &&
            !this.behaviorData.userODetails?.softLaunch &&
            ['allow-payment'].includes(this.behaviorData.userODetails?.status) &&
            Number(this.depositInfo?.amountInAccountCurrency) >= this.minLimit &&
            this.behaviorData.userODetails?.allowedAmt < this.depositInfo?.amountInfo?.nativeAmountString
        ) {
            this.amountFormGroup?.controls?.userAmount?.addValidators([spainODValidator.bind(this)()]);
            this.amountFormGroup?.controls?.userAmount?.markAsTouched();
            this.amountFormGroup.controls?.userAmount?.updateValueAndValidity();
        }
    }

    validateAnnRekycLimit() {
        if (
            this.userDataService?.userData?.configParams?.rekycSubErrorCode == '702' &&
            (Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit) <
                Number(this.selectedOption()?.paymentMethodInfo?.minDepositLimit?.value) ||
                Number(this.depositInfo?.amountInAccountCurrency) > Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit))
        ) {
            return { spainAnnRekycLimitExceeded: true };
        }
        return null;
    }

    validateAmlThreshold() {
        if (
            this.selectedOption()?.paymentMethodInfo?.type === 'CC' &&
            this.userDataService?.userData?.configParams?.esAmlThresholdLimitsEnabled === 'Y'
        ) {
            if (Number(this.selectedOption()?.paymentAccountInfo?.es_allowed_amount) < this.minLimit) {
                return { spainThreshLimitBreached: true };
            } else if (
                Number(this.depositInfo?.amountInfo?.nativeAmountString) > Number(this.selectedOption()?.paymentAccountInfo?.es_allowed_amount)
            ) {
                return { spainThreshLimitToBeBreached: true };
            }
        }
        return null;
    }

    combinedValidator() {
        const annRekycError = this.validateAnnRekycLimit();
        const amlThresholdError = this.validateAmlThreshold();
        const rekycError = this.validateRekycLimit();

        if (annRekycError) {
            this.amountFormGroup?.controls?.userAmount?.addValidators([annRekycLimitExcValidator.bind(this)()]);
            this.amountFormGroup?.controls?.userAmount?.markAsTouched();
            this.amountFormGroup?.controls?.userAmount?.updateValueAndValidity();
            this.resizeService.resizeCashier();
            if (this.amountFormGroup?.controls?.userAmount?.dirty || this.amountFormGroup?.controls?.userAmount?.touched) {
                const bannerName =
                    Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit) <
                    Number(this.selectedOption()?.paymentMethodInfo?.minDepositLimit?.value)
                        ? 'payment method restriction banner'
                        : 'you can deposit upto ' + this.userDataService?.userData?.configParams?.rekycRemainingLimit + '€ banner';
                this.gtmService.raiseEventForAnnRekycLoad(bannerName);
            }
            return;
        }
        if (this.validateEsExpiredIdCheck()) {
            this.amountFormGroup?.controls?.userAmount?.addValidators([ExpiredKycLimitValidator.bind(this)()]);
            this.amountFormGroup?.controls?.userAmount?.markAsTouched();
            this.amountFormGroup.controls?.userAmount?.updateValueAndValidity();
            this.resizeService.resizeCashier();
            if (this.amountFormGroup?.controls?.userAmount?.dirty || this.amountFormGroup?.controls?.userAmount?.touched) {
                const bannerName =
                    Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit) <
                    Number(this.selectedOption()?.paymentMethodInfo?.minDepositLimit?.value)
                        ? 'payment method restriction banner'
                        : 'you can deposit upto ' + this.userDataService?.userData?.configParams?.rekycRemainingLimit + '€ banner';
                this.gtmService.raiseEventForEsExpIdKycLoad(bannerName, 'partially verified/web verification passed');
            }
            return;
        }

        if (amlThresholdError) {
            if (amlThresholdError?.spainThreshLimitBreached) {
                this.amountFormGroup?.controls?.userAmount?.addValidators([amlThresholdExcValidator.bind(this)()]);
                this.gtmService.raiseEventForInsVerLoad(this.selectedOption()?.paymentMethodInfo?.paymentMethod + ' - over 2k banner');
            } else if (amlThresholdError?.spainThreshLimitToBeBreached) {
                this.amountFormGroup?.controls?.userAmount?.addValidators([amlThresholdToBeExcValidator.bind(this)()]);
                if (this.amountFormGroup?.controls?.userAmount?.dirty || this.amountFormGroup?.controls?.userAmount?.touched) {
                    this.gtmService.raiseEventForInsVerLoad(this.selectedOption()?.paymentMethodInfo?.paymentMethod + ' - under 2k banner');
                }
            }
            this.amountFormGroup?.controls?.userAmount?.markAsTouched();
            this.amountFormGroup.controls?.userAmount?.updateValueAndValidity();
            this.resizeService.resizeCashier();
            return;
        }
        if (rekycError) {
            this.amountFormGroup?.controls?.userAmount?.addValidators([spainRekycValidator.bind(this)()]);
            this.amountFormGroup?.controls?.userAmount?.markAsTouched();
            this.amountFormGroup.controls?.userAmount?.updateValueAndValidity();
            this.resizeService.resizeCashier();
            if (this.amountFormGroup?.controls?.userAmount?.dirty || this.amountFormGroup?.controls?.userAmount?.touched) {
                this.gtmService.raiseEventForRekycLoad('user exceeded deposit limit banner');
            }
            return;
        }
        return null;
    }
    validateEsExpiredIdCheck() {
        if (
            this.userDataService?.userData?.configParams?.isSpainExpiryKycRefreshCheckEnabled == 'Y' &&
            this.userDataService?.userData?.configParams?.rekycSubErrorCode == '604' &&
            (Number(this.depositInfo?.amountInAccountCurrency) > Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit) ||
                Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit) <
                    Number(this.selectedOption()?.paymentMethodInfo?.minDepositLimit?.value))
        ) {
            return { expiredKycLimitValidation: true };
        } else {
            return null;
        }
    }

    ngOnDestroy() {
        this.subscriptions$.map((subscription) => subscription.unsubscribe());
    }
}
